import { useEffect, useState, FormEvent, KeyboardEvent } from "react";
import { LinearProgress, useGetRecordId, useRefresh } from "react-admin";
import { useQuery } from "react-query";
import { Typography, TextField, CircularProgress, SxProps } from "@mui/material";
import { studentsProvider } from "../providers/studentsProvider";
import { grants } from "../backoffice.access_control";
import { serverAPI } from "../api/server.api";
import { LabeledAndEditable } from "../misc/LabeledAndEditable";

export function AuthProvidersField(props: { sx?: SxProps }) {
  const { sx } = props;
  const studentId = useGetRecordId().toString();
  const [editMode, setEditMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState<string>("");
  const refresh = useRefresh();
  const { data: providerData } = useQuery(
    ["students", "fetchProviderData", { id: studentId }],
    () => studentsProvider.fetchProviderData(studentId),
    {
      onSuccess: (data) => {
        setEmail(data?.find((it) => it.providerId === "password")?.email ?? "");
      },
    },
  );

  useEffect(() => {
    if (editMode) {
      setEmail(providerData?.find((it) => it.providerId === "password")?.email ?? "");
    }
  }, [editMode]);

  const toggleEditMode = () => {
    setEditMode((prevState) => !prevState);
  };

  if (!providerData) {
    return <LinearProgress timeout={0} />;
  }

  const handleSubmission = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await serverAPI.updateAuthEmail({ studentId, email });
      setEditMode(false);
      refresh();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message;
      showErrorToast(errorMessage ?? "Aktualisierung der E-Mail-Adresse fehlgeschlagen.");
      console.error("Error updating email", error);
    } finally {
      setSubmitting(false);
    }
  };

  if (editMode) {
    return (
      <form onSubmit={handleSubmission}>
        <TextField
          sx={sx}
          label="E-Mail"
          fullWidth
          InputProps={submitting ? { endAdornment: <CircularProgress size={24} /> } : {}}
          value={email}
          autoFocus
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => {
            toggleEditMode();
          }}
          onKeyDown={({ key }: KeyboardEvent<HTMLDivElement>) => {
            if (key === "Escape") {
              toggleEditMode();
            }
          }}
        />
      </form>
    );
  }

  return (
    <>
      {providerData.map((it) => {
        const isEditable = it.providerId === "password" && grants.has("editStudentPersonalData");
        return (
          <LabeledAndEditable
            key={it.providerId}
            label=""
            onClick={isEditable ? toggleEditMode : undefined}
            disableEdit={!isEditable}
          >
            <Typography variant="body2">
              {it.email} ({_providerLabels[it.providerId] ?? it.providerId})
            </Typography>
          </LabeledAndEditable>
        );
      })}
    </>
  );
}

const _providerLabels: Record<string, string> = {
  "google.com": "Google",
  "apple.com": "Apple",
  "facebook.com": "Facebook • wird nicht mehr unterstützt",
  password: "E-Mail",
};
